import React from 'react';
import i18next from 'i18next';
import Icon from '@mdi/react';
import {
    Box,
    Button,
    Collapse,
    List,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import { GlobalVariables } from '../../../../pdf/GlobalVariables';

import {
    mdiCheckCircle,
    mdiCloseCircle,
    mdiCollapseAll,
    mdiExpandAll,
    mdiSortAscending,
    mdiSortDescending,
} from '@mdi/js';

import type { IIndexable } from '../../../../pdf/Interfaces';
import { compareNumbers, toLocaleLongString } from '../../../../utils';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';

import styles from './Annotations.module.scss';
import { useSelector } from 'react-redux';
import { ApryseSDK, type ISignatureData } from '../../../../pdf/ApryseSDK';
import { eplassSliceAnnotationSidepanelRefresh } from '../../../../Redux/EplassSlice';
import { GlobalDialogs } from '../../../../pdf/GlobalDialogs';
import SignaturePropertiesDialog from '../../Dialogs/SignaturePropertiesDialog';

/**
 * Describes a tree node.
 */
interface INode<TNode extends INode<TNode>> {
    /**
     * The unique key of the node.
     */
    key: string;
    /**
     * The text of the node.
     */
    text: string;
    /**
     * The child nodes of this node.
     */
    children?: TNode[];
    /**
     * Whether the node is expanded.
     */
    isExpanded?: boolean;
    /**
     * The name of the icon to render.
     */
    icon?: JSX.Element;
}

/**
 * A node of the annotation tree.
 */
interface ISignatureNode extends INode<ISignatureNode> {
    /**
     * The annotation instance.
     */
    signatureData: ISignatureData;
    isSelected: boolean;
    isExpanded: boolean;
    isRoot: boolean;
    page?: number;
    timestamp?: number;
}

/**
 * Recursively generates the nodes for the given annotation.
 * @param signatureData The annotation element.
 * @param path The nesting path.
 * @param expanded The collapse/expand states.
 * @param selected The collapse/expand states.
 * @param children Child nodes
 */
function generateSignatureNode(
    signatureData: ISignatureData,
    key: string,
    isExpanded: boolean,
    active: number[],
    children?: ISignatureNode[],
): ISignatureNode {
    return {
        signatureData: signatureData,
        key,
        text: '?',
        isExpanded: children !== undefined ? isExpanded : false,
        isSelected: children === undefined ? active.includes(signatureData.id) : false,
        icon: renderIcon(signatureData.verificationStatus && signatureData.signer !== ''),
        children: children,
        isRoot: children !== undefined,
    };
}

function renderIcon(isValid: boolean): JSX.Element {
    if (isValid) {
        return <Icon path={mdiCheckCircle} color={'green'} />;
    }
    return <Icon path={mdiCloseCircle} color={'red'} />;
}

/**
 * Component that shows a configurable menu structure in a Microsoft-Office-like structure.
 */
export default () => {
    const forceRefresh = useSelector(eplassSliceAnnotationSidepanelRefresh);
    const [sortBy, setSortBy] = React.useState<'Ascending' | 'Descending'>('Ascending');
    const [expanded, setExpanded] = React.useState<IIndexable<boolean>>({});

    const toggleExpanded = (key: string) => {
        const newValue = {};
        newValue[key] = !expanded[key];
        setExpanded({
            ...expanded,
            ...newValue,
        });
    };

    // The root nodes
    const rootNodes = React.useMemo(() => {
        const signatureFieldIDs = Object.keys(ApryseSDK.signatureData);
        if (signatureFieldIDs.length === 0) {
            return [];
        }

        const path = [];
        const active = [];
        const results: ISignatureNode[] = [];
        const newExpaned: IIndexable<boolean> = { ...expanded };

        const uniqueDates = signatureFieldIDs
            .map(id => {
                const d: Date = ApryseSDK.signatureData[id].date;
                d.setHours(0, 0, 0, 0);
                return d.getTime();
            })
            .filter((value, index, self) => self.indexOf(value) === index);

        uniqueDates.forEach(ts => {
            if (newExpaned['root_timestamp_' + ts] === undefined) {
                newExpaned['root_timestamp_' + ts] = true;
            }
            const expanded = newExpaned['root_timestamp_' + ts] ?? false;

            const filteredSignatures = signatureFieldIDs
                .map(id => {
                    const sig: ISignatureData = ApryseSDK.signatureData[id];
                    const d = sig.date;
                    d.setHours(0, 0, 0, 0);
                    return d.getTime() === ts ? sig : null;
                })
                .filter(sig => sig != null);

            const root = generateSignatureNode(
                filteredSignatures[0],
                'root_timestamp_' + ts,
                expanded,
                active,
                filteredSignatures.map((sig, i) =>
                    generateSignatureNode(sig, [...path, i].join('_'), expanded, active),
                ),
            );
            const d = new Date(ts);
            root.text = d.getFullYear() === 1970 ? 'Unsigned' : toLocaleLongString(d);
            root.timestamp = ts;
            results.push(root);
        });

        // Save updates
        setExpanded(newExpaned);

        switch (sortBy) {
            case 'Ascending':
                return results.sort((a, b) => compareNumbers(a.timestamp, b.timestamp, false));
            case 'Descending':
                return results.sort((a, b) => compareNumbers(a.timestamp, b.timestamp, true));
        }

        return results;
    }, [forceRefresh, sortBy]);

    const sortBarItems = React.useMemo(() => {
        return [
            <Button
                id={GlobalVariables.isForTestRun ? 'annotsSidebarMenuBtnSortAscending' : undefined}
                className={[
                    styles.iconButton,
                    sortBy === 'Ascending' ? styles.iconButtonSelected : null,
                ].join(' ')}
                variant="outlined"
                key="sortAscending"
                title={i18next.t('sidebar.popups.annotations.sort.ascending')}
                onClick={() => {
                    if (sortBy !== 'Ascending') {
                        setSortBy('Ascending');
                    }
                }}
            >
                <Icon path={mdiSortAscending} />
            </Button>,
            <Button
                id={GlobalVariables.isForTestRun ? 'annotsSidebarMenuBtnSortDescending' : undefined}
                className={[
                    styles.iconButton,
                    sortBy === 'Descending' ? styles.iconButtonSelected : null,
                ].join(' ')}
                variant="outlined"
                key="sortDescending"
                title={i18next.t('sidebar.popups.annotations.sort.descending')}
                onClick={() => {
                    if (sortBy !== 'Descending') {
                        setSortBy('Descending');
                    }
                }}
            >
                <Icon path={mdiSortDescending} />
            </Button>,
            <Button
                id={GlobalVariables.isForTestRun ? 'annotsSidebarMenuBtnExpand' : undefined}
                className={styles.iconButton}
                variant="outlined"
                key="expand"
                title={i18next.t('sidebar.popups.annotations.action.expand')}
                onClick={() => {
                    const newExpaned: IIndexable<boolean> = { ...expanded };
                    rootNodes.forEach(n => {
                        newExpaned[n.key] = true;
                    });
                    setExpanded(newExpaned);
                }}
            >
                <Icon path={mdiExpandAll} />
            </Button>,
            <Button
                id={GlobalVariables.isForTestRun ? 'annotsSidebarMenuBtnCollapse' : undefined}
                className={styles.iconButton}
                variant="outlined"
                key="collapse"
                title={i18next.t('sidebar.popups.annotations.action.collapse')}
                onClick={() => {
                    const newExpaned: IIndexable<boolean> = { ...expanded };
                    rootNodes.forEach(n => {
                        newExpaned[n.key] = false;
                    });
                    setExpanded(newExpaned);
                }}
            >
                <Icon path={mdiCollapseAll} />
            </Button>,
        ];
    }, [sortBy, rootNodes]);

    const menu = React.useMemo(() => {
        return (
            <Stack className={styles.sidebarMenuArea} direction="row" spacing={1} pl={2} pr={2}>
                {sortBarItems}
            </Stack>
        );
    }, [sortBarItems]);

    const signatureInfo = (signatureData: ISignatureData) => {
        if (!signatureData.signer) {
            return (
                <ListItemText>
                    <Typography sx={{ fontSize: '12px' }}>
                        {i18next.t('signaturesPanel.unsigned')}
                    </Typography>
                </ListItemText>
            );
        }

        return (
            <ListItemText>
                <Typography sx={{ fontSize: '12px', wordWrap: 'break-word', textWrap: 'wrap' }}>
                    {i18next.t('signaturesPanel.signedBy', {
                        signer: signatureData.signer ?? i18next.t('signaturesPanel.unsigned'),
                    })}
                    <br />
                    {i18next.t('signaturesPanel.signedTime', {
                        time: signatureData.timeOfTrustVerification
                            ? signatureData.timeOfTrustVerification.toLocaleTimeString()
                            : i18next.t('signaturesPanel.unknown'),
                    })}
                    <br />
                    {i18next.t('signaturesPanel.status', {
                        signer: signatureData.verificationStatus ? 'valid' : 'not valid',
                    })}
                    <br />
                    {i18next.t('signaturesPanel.modifications')}
                    <br />
                    {i18next.t('signaturesPanel.verificationResult', {
                        result: signatureData.verificationStatus ?? 'not verified',
                    })}
                    <br />
                    {i18next.t('signaturesPanel.verificationTime')}
                    <Button
                        style={{
                            display: 'block',
                        }}
                        onClick={() => {
                            GlobalDialogs.showDialog(
                                i18next.t('signaturePropsDialog.title.dialog'),
                                <SignaturePropertiesDialog
                                    onClose={() => GlobalDialogs.closeDialog()}
                                    signatureId={signatureData.id}
                                />,
                            );
                        }}
                    >
                        {i18next.t('signaturesPanel.openProperties')}
                    </Button>
                </Typography>
            </ListItemText>
        );
    };

    const listElements = React.useMemo(() => {
        const elements: JSX.Element[] = [];
        rootNodes.forEach(node => {
            elements.push(
                <MenuItem
                    className={styles.sidebarMainListItem}
                    key={`listButton-${node.key}`}
                    onClick={() => toggleExpanded(node.key)}
                >
                    {expanded[node.key] ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                    <Stack direction="row" display="contents">
                        <ListItemText
                            primary={node.text}
                            secondaryTypographyProps={{ align: 'right', color: 'red' }}
                        />
                        <span
                            style={{
                                textAlign: 'center',
                                color: 'red',
                                border: '1px solid red',
                                borderRadius: '50%',
                                height: '1.5em',
                                width: '1.5em',
                                fontSize: '12px',
                            }}
                        >
                            {node.children?.length}
                        </span>
                    </Stack>
                </MenuItem>,
            );
            elements.push(
                <Collapse
                    key={`listCollapse-${node.key}`}
                    in={expanded[node.key]}
                    timeout="auto"
                    unmountOnExit
                >
                    <List component="div">
                        {node.children?.map(child => (
                            <MenuItem
                                className={styles.sidebarSecondaryListItem}
                                key={child.key}
                                sx={{ pl: 4 }}
                            >
                                <ListItemIcon className={styles.sidebarSecondaryListIcon}>
                                    {child.icon}
                                </ListItemIcon>
                                {signatureInfo(child.signatureData)}
                            </MenuItem>
                        ))}
                    </List>
                </Collapse>,
            );
        });
        return elements;
    }, [rootNodes, expanded]);

    return (
        <Stack sx={{ flexGrow: 1 }}>
            <Box>{menu}</Box>
            <Stack className={styles.navWrapper}>
                <List
                    className={styles.resultList}
                    component="nav"
                    sx={{
                        overflowX: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'absolute',
                        top: '0',
                    }}
                >
                    {listElements}
                </List>
            </Stack>
        </Stack>
    );
};
